import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Pill, Loading, ErrorNote, Meter } from '../components/ui';
import { useRight } from '../lib/auth';
import { PLANS, PCN_DISCOUNT, money, quote, type PlanId } from '@inbrief/shared';

interface PlanData {
  organisation: { plan_id: PlanId; founding_customer: boolean; custom_pricing: boolean; name: string };
  practiceCount: number;
  currentQuote: ReturnType<typeof quote> | null;
  translations: { used: number; limit: number };
  modelSpendByFeature: { feature: string; pence: string }[];
  spend: { usedPence: number; capPence: number; remainingPence: number; exceeded: boolean };
}

export function Plan() {
  const canManage = useRight('settings');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['plan'],
    queryFn: () => api.get<PlanData>('/api/plan'),
  });

  const setCap = useMutation({
    mutationFn: (capGbp: number | null) => api.patch('/api/plan/spend-cap', { capGbp }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan'] }),
  });

  const [calcPlan, setCalcPlan] = useState<PlanId>('complete');
  const [calcPractices, setCalcPractices] = useState(6);
  const [calcFounding, setCalcFounding] = useState(true);

  if (isLoading) return <Loading what="Loading your plan" />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  // Computed in the browser from the same shared module the server uses, so
  // the calculator and the invoice cannot disagree.
  const calculated = quote(calcPlan, calcPractices, calcFounding);

  return (
    <>
      <PageHeader
        title="Plan & Access"
        description={`${data.organisation.name} — what you are on, what it costs, and what you have used.`}
      />

      <div className="two-column">
        <Panel title="Your plan" icon="◈">
          <div className="row-gap" style={{ marginBottom: 12 }}>
            <Pill tone="approved">
              InBrief {PLANS[data.organisation.plan_id].name}
            </Pill>
            {data.organisation.founding_customer ? <Pill tone="info">Founding customer</Pill> : null}
            {data.organisation.custom_pricing ? <Pill tone="info">Agreed pricing</Pill> : null}
          </div>

          {data.currentQuote ? (
            <div className="calc-out">
              {data.currentQuote.lines.map((l, i) => (
                <div className="calc-line" key={i}>
                  <span>{l.label}</span>
                  <span>{l.amountGbp === null ? '—' : money(l.amountGbp)}</span>
                </div>
              ))}
              <div className="calc-line is-total">
                <span>Annual total, excluding VAT</span>
                <span>{money(data.currentQuote.totalGbp!)}</span>
              </div>
              <div className="calc-line" style={{ fontSize: 11 }}>
                <span>
                  {money(data.currentQuote.perPracticeGbp!)} per practice a year, about{' '}
                  {money(data.currentQuote.perPracticeMonthlyGbp!)} a month.
                </span>
              </div>
            </div>
          ) : (
            <p className="field-hint">
              Your organisation is on individually agreed pricing, so no list price applies.
            </p>
          )}
        </Panel>

        <Panel title="Translation allowance" icon="◷">
          <div className="kv">
            <span className="fact-label">Used this year</span>
            <span className="fact-value">
              {data.translations.used.toLocaleString('en-GB')} of{' '}
              {data.translations.limit.toLocaleString('en-GB')}
            </span>
          </div>
          <Meter used={data.translations.used} limit={data.translations.limit} />
          <p className="field-hint">
            {PLANS[data.organisation.plan_id].translationsPerPracticePerYear} per practice per year,
            across {data.practiceCount} practices.
          </p>
        </Panel>
      </div>

      <Panel title="What each plan includes" icon="≡">
        <div className="table-wrap">
          <table className="dt">
            <caption className="visually-hidden">Plan comparison</caption>
            <thead>
              <tr>
                <th scope="col">&nbsp;</th>
                <th scope="col">{PLANS.core.name}</th>
                <th scope="col">{PLANS.complete.name}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Per practice, monthly</th>
                <td>{money(PLANS.core.monthlyGbp)}</td>
                <td>{money(PLANS.complete.monthlyGbp)}</td>
              </tr>
              <tr>
                <th scope="row">Per practice, annually</th>
                <td>{money(PLANS.core.annualGbp)}</td>
                <td>{money(PLANS.complete.annualGbp)}</td>
              </tr>
              <tr>
                <th scope="row">Translations per practice per year</th>
                <td>{PLANS.core.translationsPerPracticePerYear}</td>
                <td>{PLANS.complete.translationsPerPracticePerYear}</td>
              </tr>
              <tr>
                <th scope="row">Human-reviewed translation credits</th>
                <td>{PLANS.core.humanReviewedTranslationsPerPracticePerYear || '—'}</td>
                <td>{PLANS.complete.humanReviewedTranslationsPerPracticePerYear} per practice</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="vat-note">All prices exclude VAT.</p>
      </Panel>

      <Panel title="Work out a price" icon="=">
        <div className="ask-row">
          <div className="field">
            <label className="field-label" htmlFor="calcPlan">Plan</label>
            <select id="calcPlan" value={calcPlan} onChange={(e) => setCalcPlan(e.target.value as PlanId)}>
              <option value="core">{PLANS.core.name}</option>
              <option value="complete">{PLANS.complete.name}</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="calcPractices">Practices</label>
            <select
              id="calcPractices"
              value={calcPractices}
              onChange={(e) => setCalcPractices(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <option key={n} value={n}>{n === 7 ? '7 or more' : n}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="calcFounding">Founding customer</label>
            <input
              id="calcFounding"
              type="checkbox"
              checked={calcFounding}
              onChange={(e) => setCalcFounding(e.target.checked)}
            />
          </div>
        </div>

        <div className="calc-out">
          {calculated.custom ? (
            <>
              <div className="calc-line">
                <span>{PLANS[calcPlan].name}, 7 or more practices</span>
                <span>Custom pricing</span>
              </div>
              <div className="calc-line is-total"><span>Annual total</span><span>By agreement</span></div>
              <div className="calc-line" style={{ fontSize: 11 }}>
                Seven or more practices are priced individually, so no list price applies.
              </div>
            </>
          ) : (
            <>
              {calculated.lines.map((l, i) => (
                <div className="calc-line" key={i}>
                  <span>{l.label}</span>
                  <span>{l.amountGbp === 0 ? '—' : money(l.amountGbp!)}</span>
                </div>
              ))}
              <div className="calc-line is-total">
                <span>Annual total, excluding VAT</span>
                <span>{money(calculated.totalGbp!)}</span>
              </div>
              <div className="calc-line" style={{ fontSize: 11 }}>
                <span>
                  {money(calculated.perPracticeGbp!)} per practice a year, about{' '}
                  {money(calculated.perPracticeMonthlyGbp!)} a month.
                </span>
              </div>
            </>
          )}
        </div>

        <p className="field-hint">
          PCN discount by practice count:{' '}
          {Object.entries(PCN_DISCOUNT).map(([n, pct]) => `${n}: ${pct}%`).join(' · ')}
        </p>
      </Panel>

      <Panel
        title="Processing cost this month"
        icon="£"
        actions={
          canManage ? (
            <button
              className="btn btn-secondary btn-small"
              onClick={() => {
                const current = (data.spend.capPence / 100).toFixed(0);
                const answer = window.prompt(
                  'Monthly processing limit in pounds. Leave blank to use the deployment default.',
                  current,
                );
                if (answer === null) return;
                const trimmed = answer.trim();
                setCap.mutate(trimmed === '' ? null : Number(trimmed));
              }}
            >
              Change limit
            </button>
          ) : null
        }
      >
        <div className="kv">
          <span className="fact-label">Used this month</span>
          <span className="fact-value">
            £{(data.spend.usedPence / 100).toFixed(2)} of £{(data.spend.capPence / 100).toFixed(2)}
          </span>
        </div>
        <Meter used={data.spend.usedPence} limit={data.spend.capPence} />

        {data.spend.exceeded ? (
          <div className="warn-box">
            This organisation has reached its processing limit for this month. Extraction, Ask,
            drafting and translation are paused until next month, or until an administrator raises
            the limit. Everything already extracted stays available.
          </div>
        ) : data.spend.remainingPence < data.spend.capPence * 0.15 ? (
          <div className="note-box">
            Less than 15% of this month's processing limit remains. Raise it if you have more
            contracts to load before month end.
          </div>
        ) : null}

        <ErrorNote error={setCap.error} />

        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="dt">
            <caption className="visually-hidden">Model cost by feature</caption>
            <thead><tr><th scope="col">Feature</th><th scope="col">Cost</th></tr></thead>
            <tbody>
              {data.modelSpendByFeature.length === 0 ? (
                <tr><td colSpan={2}>Nothing processed yet this month.</td></tr>
              ) : data.modelSpendByFeature.map((r) => (
                <tr key={r.feature}>
                  <th scope="row">{r.feature}</th>
                  <td>£{(Number(r.pence) / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="field-hint">
          What it costs InBrief to process your documents. Shown for transparency; it does not
          affect your subscription. The limit is a hard stop, so one bulk upload cannot produce a
          surprise.
        </p>
      </Panel>

      {/*
        Every customer of this product is a public sector body with obligations
        under the 2018 accessibility regulations, and a DTAC assessor will ask
        for this. It says what has been tested and what has not, because a
        statement that overstates conformance is worse than none.
      */}
      <Panel title="Accessibility" icon="◉">
        <p>
          InBrief is <strong>partially compliant</strong> with WCAG 2.2 AA. An automated A and
          AA audit runs over twelve states — the sign-in screen, all ten sections, and the
          mobile menu — as part of the test suite, and reports no violations.
        </p>
        <div className="warn-box">
          Automated checking finds roughly a third of real accessibility problems. InBrief has
          not yet been tested with a screen reader, with users of assistive technology, or by
          an independent auditor, so it is described as partially compliant rather than
          compliant.
        </div>
        <p className="field-hint">
          Known limitations include the original contract PDFs, which are third-party NHS
          publications and often untagged — the extracted text InBrief produces from them is
          accessible, and no task requires reading the PDF itself. The full statement, and how
          to report a problem, is in <code>ACCESSIBILITY.md</code>.
        </p>
      </Panel>
    </>
  );
}
